import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OtaReviewPage from './OtaReviewPage';
import { adminHeaders } from '../../lib/adminAuth';
import './CalendarPage.css';

import { dowName } from '../../lib/adminI18n';
import { tx } from '../../lib/adminI18n';
interface Property {
  id: string;
  name: string;
  abbreviation?: string | null;   // カレンダー表示用の略語（任意）
  booking_prefix?: string | null; // 物件コード（SD, FT 等）。略語未設定時のフォールバック
}

interface Reservation {
  id: string;
  property_id: string;
  guest_name: string;
  checkin_date: string;
  checkout_date: string;
  status: string;
  total_price: number;
}

interface BlockedDate {
  date: string;
  source: string;
  reservation_id: string | null;
}

interface CalendarEntry {
  property: Property;
  reservations: Reservation[];
  blocked_dates?: BlockedDate[];
}

// YYYY-MM-DD 文字列へ（ローカルタイムゾーンで）
function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// カレンダー表示用の物件ラベル。略語(abbreviation)があればそれを、
// 無ければ物件コード(booking_prefix: SD, FT)を、どちらも無ければ物件名を表示する。
function propLabel(p: Property): string {
  const abbr = (p.abbreviation || '').trim();
  if (abbr) return abbr;
  const prefix = (p.booking_prefix || '').trim();
  if (prefix) return prefix;
  return p.name;
}

function CalendarPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 画面内タブ: カレンダー / OTA同期の確認（?view=ota。旧URL /admin/ota-review からもここへ来る）
  const [params, setParams] = useSearchParams();
  const view: 'calendar' | 'ota' = params.get('view') === 'ota' ? 'ota' : 'calendar';
  const setView = (v: 'calendar' | 'ota') => {
    const n = new URLSearchParams();
    if (v === 'ota') { n.set('view', 'ota'); const tb = params.get('tab'); if (tb) n.set('tab', tb); }
    setParams(n, { replace: true });
  };
  // OTA確認の未処理件数（タブのバッジ表示用）
  const [otaCount, setOtaCount] = useState(0);
  useEffect(() => {
    const loadOta = () => {
      fetch('/api/admin/ota-review/summary', { headers: adminHeaders(), credentials: 'include' })
        .then((r) => (r.ok ? r.json() : {}))
        .then((d: any) => setOtaCount(['incomplete', 'blocks', 'review', 'pending', 'date_changed']
          .reduce((a, k) => a + (Number(d?.[k]) || 0), 0)))
        .catch(() => {});
    };
    loadOta();
    const onChanged = () => { loadOta(); void fetchCalendar(); };
    window.addEventListener('ota-review-changed', onChanged);
    return () => window.removeEventListener('ota-review-changed', onChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 表示中の月（その月の1日）
  const [current, setCurrent] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [calendar, setCalendar] = useState<CalendarEntry[]>([]);
  const [loading, setLoading] = useState(true);
  // 選択中の物件ID（'' = すべて）
  const [selectedProperty, setSelectedProperty] = useState<string>('');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  // 月の開始・終了日
  const monthStart = useMemo(
    () => new Date(current.getFullYear(), current.getMonth(), 1),
    [current]
  );
  const monthEnd = useMemo(
    () => new Date(current.getFullYear(), current.getMonth() + 1, 0),
    [current]
  );

  useEffect(() => {
    fetchCalendar();
  }, [current]);

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const start = fmtDate(monthStart);
      const end = fmtDate(monthEnd);
      const response = await fetch(
        `/api/admin/calendar?start=${start}&end=${end}`,
        { headers: adminHeaders() }
      );
      if (!response.ok) throw new Error('Failed to fetch calendar');
      const data = await response.json();
      setCalendar(data);
    } catch (err) {
      console.error('Calendar error:', err);
      setCalendar([]);
    } finally {
      setLoading(false);
    }
  };

  // 月グリッドの日付配列（前月・翌月の余白含む、週頭=日曜）
  const gridDays = useMemo(() => {
    const days: Date[] = [];
    const firstDow = monthStart.getDay(); // 0=日
    // 前月分の余白
    for (let i = firstDow - 1; i >= 0; i--) {
      days.push(new Date(current.getFullYear(), current.getMonth(), -i));
    }
    // 当月
    for (let d = 1; d <= monthEnd.getDate(); d++) {
      days.push(new Date(current.getFullYear(), current.getMonth(), d));
    }
    // 翌月分の余白（6週=42マスに揃える）
    while (days.length % 7 !== 0) {
      const last = days[days.length - 1];
      days.push(new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1));
    }
    return days;
  }, [current, monthStart, monthEnd]);

  // 表示対象の物件（タブ選択でフィルタ）
  const visibleEntries = useMemo(() => {
    if (!selectedProperty) return calendar;
    return calendar.filter((e) => e.property.id === selectedProperty);
  }, [calendar, selectedProperty]);

  // 指定日にかかる予約を集める（複数物件をまたいで）。連結表現用に当日の位置情報も付ける。
  const reservationsOnDate = (date: Date): Array<Reservation & { property_name: string }> => {
    const ds = fmtDate(date);
    const out: Array<Reservation & { property_name: string }> = [];
    for (const entry of visibleEntries) {
      for (const r of entry.reservations) {
        // 滞在期間 [checkin, checkout) に ds が含まれるか
        if (r.checkin_date <= ds && ds < r.checkout_date) {
          out.push({ ...r, property_name: propLabel(entry.property) });
        }
      }
    }
    return out;
  };

  // その予約の「当日(ds)」が滞在期間のどの位置か。連結風スタイルの角丸・左右マージン制御に使う。
  //   ・isStart: チェックイン日（左端を丸める）
  //   ・isEnd  : 最終滞在日＝チェックアウト前日（右端を丸める）
  //   ・週の境界（日曜=左端, 土曜=右端）でも端として丸める。
  const stayPosition = (r: Reservation, date: Date) => {
    const ds = fmtDate(date);
    const prev = fmtDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1));
    const next = fmtDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1));
    // 滞在は [checkin, checkout)。最終滞在日は checkout の前日。
    const isStart = r.checkin_date === ds;
    const isEnd = !(next < r.checkout_date); // 翌日が滞在外＝当日が最終滞在日
    const dow = date.getDay();
    const weekLeft = dow === 0;   // 日曜＝週の左端
    const weekRight = dow === 6;  // 土曜＝週の右端
    // 連続の途中かどうか（前日も当日も滞在中）
    const contPrev = r.checkin_date <= prev && prev < r.checkout_date;
    return { isStart, isEnd, weekLeft, weekRight, contPrev };
  };

  // 指定日の外部ブロック（OTA=Airbnb等の取込。reservation_idがnull=自社予約以外）
  const blocksOnDate = (date: Date): Array<{ property_id: string; property_name: string; source: string }> => {
    const ds = fmtDate(date);
    const out: Array<{ property_id: string; property_name: string; source: string }> = [];
    for (const entry of visibleEntries) {
      for (const b of entry.blocked_dates || []) {
        // 自社予約由来(reservation_id有り)は予約バーで表示済みなので除外
        if (b.reservation_id) continue;
        if (b.date === ds) {
          out.push({ property_id: entry.property.id, property_name: propLabel(entry.property), source: b.source });
        }
      }
    }
    return out;
  };

  // 物件ごとの識別色。全物件表示時に各物件のOTA Stopを色分けする。
  // 色相環で大きく離れた色を順に並べ、隣り合う物件が似た色にならないようにする
  // （ピンクと赤のような紛らわしい組み合わせを避ける）。
  const PROPERTY_COLORS = [
    '#2563eb', // 青
    '#dc2626', // 赤
    '#16a34a', // 緑
    '#ea580c', // オレンジ
    '#7c3aed', // 紫
    '#0d9488', // ティール
    '#d97706', // アンバー
    '#db2777', // ピンク（最後に配置し、赤と隣接させない）
  ];
  // 物件コード（booking_prefix）ごとの固定色。並び順に依存せず、同じ物件は常に同じ色。
  //   色相が大きく離れた組み合わせ（青/緑/オレンジ）を主要物件に割り当てる。
  //   未知のコードは PROPERTY_COLORS へフォールバック（コード文字列から安定的に決定）。
  const FIXED_PREFIX_COLORS: Record<string, string> = {
    SD: '#2563eb', // Stay Dream = 青
    FT: '#16a34a', // Flower Tea = 緑
    GW: '#ea580c', // Great View = オレンジ
  };
  // 物件ID → 色。booking_prefix に固定色があればそれを、無ければコード/IDから安定的に割り当てる。
  const propertyColor = (propertyId: string): string => {
    const entry = calendar.find((e) => e.property.id === propertyId);
    const prefix = (entry?.property.booking_prefix || '').trim().toUpperCase();
    if (prefix && FIXED_PREFIX_COLORS[prefix]) return FIXED_PREFIX_COLORS[prefix];
    // フォールバック: コード（無ければID）の文字コード和でパレットから安定的に選ぶ。
    const key = prefix || propertyId;
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = (hash + key.charCodeAt(i)) % PROPERTY_COLORS.length;
    return PROPERTY_COLORS[hash];
  };

  const sourceLabel = (source: string): string => {
    const map: Record<string, string> = {
      ota: 'Airbnb/OTA',
      airbnb: 'Airbnb',
      booking: 'Booking.com',
      manual: tx('cal.0'),
    };
    return map[source] || source;
  };

  const monthLabel = tx('cal.1', { p0: current.getFullYear(), p1: current.getMonth() + 1 });
  const today = fmtDate(new Date());
  const weekLabels = [0, 1, 2, 3, 4, 5, 6].map(dowName);

  const goPrev = () => setCurrent(new Date(current.getFullYear(), current.getMonth() - 1, 1));
  const goNext = () => setCurrent(new Date(current.getFullYear(), current.getMonth() + 1, 1));
  const goToday = () => {
    const now = new Date();
    setCurrent(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  // OTAカレンダー（Airbnb等）を今すぐ取り込み、完了後にカレンダーを再取得
  const syncNow = async () => {
    // 「すべての物件」選択時は無効。特定物件が選択されているときのみ、その物件を同期する。
    if (!selectedProperty) return;
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await fetch(`/api/admin/ical/sync/${selectedProperty}`, {
        method: 'POST',
        headers: adminHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || tx('cal.2'));
      setSyncMsg(tx('cal.3', { p0: data.imported }));
      await fetchCalendar();
    } catch (e: any) {
      setSyncMsg(tx('cal.4', { p0: e?.message || e }));
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMsg(null), 5000);
    }
  };

  const statusClass = (status: string) => {
    if (status === 'CONFIRMED') return 'ev-confirmed';
    if (status === 'PENDING_TRANSFER') return 'ev-transfer';
    return 'ev-other';
  };

  return (
    <div className="calendar-page">
      <h1>{t('admin.calendar')}</h1>

      <div className="cal-view-tabs">
        <button type="button" className={`cal-view-tab ${view === 'calendar' ? 'active' : ''}`} onClick={() => setView('calendar')}>
          {t('admin.calendar')}
        </button>
        <button type="button" className={`cal-view-tab ${view === 'ota' ? 'active' : ''}`} onClick={() => setView('ota')}>
          {tx('nav.otaReview')}{otaCount > 0 && <span className="cal-view-badge">{otaCount}</span>}
        </button>
      </div>

      {view === 'ota' ? <OtaReviewPage embedded /> : (<>

      {/* 月ナビゲーション */}
      <div className="calendar-toolbar">
        <div className="month-nav">
          <button className="nav-btn" onClick={goPrev} aria-label={tx('cal.5')}>‹</button>
          <span className="month-label">{monthLabel}</span>
          <button className="nav-btn" onClick={goNext} aria-label={tx('cal.6')}>›</button>
          <button className="today-btn" onClick={goToday}>{tx('cal.7')}</button>
          <button
            className="sync-now-btn"
            onClick={syncNow}
            disabled={syncing || !selectedProperty}
            title={!selectedProperty ? tx('cal.8') : tx('cal.9')}
          >
            {syncing ? tx('cal.10') : tx('cal.11')}
          </button>
        </div>

        {/* 物件切替タブ */}
        <div className="property-tabs">
          <button
            className={`tab ${selectedProperty === '' ? 'active' : ''}`}
            onClick={() => setSelectedProperty('')}
          >
            {tx('cal.12')}
          </button>
          {calendar.map((entry) => (
            <button
              key={entry.property.id}
              className={`tab ${selectedProperty === entry.property.id ? 'active' : ''}`}
              onClick={() => setSelectedProperty(entry.property.id)}
              title={entry.property.name}
            >
              {propLabel(entry.property)}
            </button>
          ))}
        </div>
      </div>

      {syncMsg && <div className="sync-message">{syncMsg}</div>}

      {/* 凡例 */}
      <div className="legend">
        <span className="legend-item"><i className="dot ev-confirmed" /> {tx('cal.13')}</span>
        <span className="legend-item"><i className="dot ev-transfer" /> {tx('cal.14')}</span>
        {selectedProperty ? (
          <span className="legend-item"><i className="dot ev-blocked" /> {tx('cal.15')}</span>
        ) : (
          <>
            <span className="legend-sep">{tx('cal.16')}</span>
            {calendar.map((entry) => (
              <span key={entry.property.id} className="legend-item" title={entry.property.name}>
                <i
                  className="dot"
                  style={{ background: propertyColor(entry.property.id) }}
                />
                {propLabel(entry.property)}
              </span>
            ))}
          </>
        )}
      </div>

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div className="calendar-grid">
          {weekLabels.map((w, i) => (
            <div key={`h-${i}`} className={`grid-head ${i === 0 ? 'sun' : ''} ${i === 6 ? 'sat' : ''}`}>
              {w}
            </div>
          ))}

          {gridDays.map((date, idx) => {
            const inMonth = date.getMonth() === current.getMonth();
            const ds = fmtDate(date);
            const evs = reservationsOnDate(date);
            const blocks = blocksOnDate(date);
            const dow = date.getDay();
            return (
              <div
                key={idx}
                className={`grid-cell ${inMonth ? '' : 'out-month'} ${ds === today ? 'today' : ''} ${dow === 0 ? 'sun' : ''} ${dow === 6 ? 'sat' : ''}`}
              >
                <div className="cell-date">{date.getDate()}</div>
                <div className="cell-events">
                  {evs.slice(0, 3).map((ev) => (
                    (() => {
                      // 連結風スタイル: 当日の滞在位置で角丸・左右の張り出しを制御し、横につながって見せる。
                      const pos = stayPosition(ev, date);
                      const roundLeft = pos.isStart || pos.weekLeft;   // 開始日 or 週頭は左を丸める
                      const roundRight = pos.isEnd || pos.weekRight;   // 最終日 or 週末は右を丸める
                      const linkClass = [
                        'event', 'ev-stay', statusClass(ev.status),
                        roundLeft ? 'stay-left' : '',
                        roundRight ? 'stay-right' : '',
                        !roundLeft ? 'stay-cont-l' : '',
                        !roundRight ? 'stay-cont-r' : '',
                      ].filter(Boolean).join(' ');
                      return (
                        <button
                          key={ev.id + ev.property_id}
                          className={linkClass}
                          style={{ background: propertyColor(ev.property_id) }}
                          title={`${ev.property_name} / ${ev.guest_name} / ${ev.checkin_date}〜${ev.checkout_date}`}
                          onClick={() => navigate(`/admin/reservations?highlight=${ev.id}`)}
                        >
                          {/* 物件名・ゲスト名はチェックイン日（開始セル）にのみ表示し、中日は帯だけにして連結感を出す */}
                          {!selectedProperty && pos.isStart && <span className="event-prop">{ev.property_name}</span>}
                          {pos.isStart
                            ? <span className="event-guest">{ev.guest_name}</span>
                            : <span className="event-guest event-cont">&nbsp;</span>}
                        </button>
                      );
                    })()
                  ))}
                  {evs.length > 3 && (
                    <span className="more">+{evs.length - 3}{tx('cal.18')}</span>
                  )}
                  {blocks.slice(0, 2).map((b, i) => {
                    // 全物件表示時は物件ごとの色で斜線ハッチ、単一物件表示時は既定のグレー
                    const c = propertyColor(b.property_id);
                    const style = !selectedProperty
                      ? { background: `repeating-linear-gradient(45deg, ${c}, ${c} 4px, ${c}cc 4px, ${c}cc 8px)` }
                      : undefined;
                    return (
                      <div
                        key={`blk-${i}`}
                        className="event ev-blocked"
                        style={style}
                        title={tx('cal.19', { p0: b.property_name, p1: sourceLabel(b.source) })}
                      >
                        {!selectedProperty && <span className="event-prop">{b.property_name}</span>}
                        <span className="event-guest">{tx('cal.20')}{sourceLabel(b.source)}{tx('cal.21')}</span>
                      </div>
                    );
                  })}
                  {blocks.length > 2 && (
                    <span className="more">+{blocks.length - 2} Stop</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>)}
    </div>
  );
}

export default CalendarPage;
